import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from '@/contexts/Authentication';
import { PageLoading } from '@/components';
import { STAFF_ROLES } from '@/constants/roles';
import type { Role } from '@/types/user';

/**
 * Protege rotas:
 *  - sem sessão → /entrar?next=...
 *  - paciente com cadastro incompleto → /cadastro (quando `registered`)
 *  - perfil de equipe sem 2FA confirmado → /conta/seguranca/2fa (obrigatório)
 *  - sem o perfil → /sem-permissao
 */
export function RequireAuth({ children, roles, registered }: { children: ReactNode; roles?: Role[]; registered?: boolean }) {
  const { me, loading } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoading />;
  if (!me) return <Navigate to={`/entrar?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (registered && !me.registration.completed) return <Navigate to="/cadastro" replace />;
  if (roles?.length) {
    const allowed = roles.filter((r) => me.roles.includes(r));
    if (!allowed.length) return <Navigate to="/sem-permissao" replace />;
    const staffOnly = allowed.every((r) => STAFF_ROLES.includes(r));
    if (staffOnly && !me.mfa.verified) {
      return <Navigate to={`/conta/seguranca/2fa?obrigatorio=1&next=${encodeURIComponent(location.pathname)}`} replace />;
    }
  }
  return <>{children}</>;
}

/** Só para visitantes (login/cadastro): quem já está logado vai para a sua área. */
export function GuestOnly({ children }: { children: ReactNode }) {
  const { me, loading } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoading />;
  if (me) {
    const next = new URLSearchParams(location.search).get('next');
    if (next?.startsWith('/')) return <Navigate to={next} replace />;
    return <Navigate to={homeFor(me.roles, me.registration.completed)} replace />;
  }
  return <>{children}</>;
}

/** Área inicial depois do login. */
export function homeFor(roles: Role[], registered: boolean): string {
  if (roles.includes('MANAGER')) return '/gestor';
  if (roles.includes('FINANCE')) return '/financeiro';
  if (roles.includes('SUPPORT')) return '/suporte';
  if (roles.includes('FRANCHISEE')) return '/franqueado';
  if (roles.includes('DOCTOR')) return '/medico';
  return registered ? '/inicio' : '/cadastro';
}
